import {
  Box,
  FormControl,
  FormLabel,
  Grid2 as Grid,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Divider,
  MenuItem,
} from "@mui/material";
import { FormProvider } from "react-hook-form";
import { SaveButton } from "@/organism";
import {
  SubscriptionProductDto,
  SubscriptionProductUpdateRequestDto,
} from "@/dto/SubscriptionDto";
import useEditForm from "./useEditForm";
import { Yn } from "@/constants/code";

interface EditFormProps {
  subscriptionInfo: SubscriptionProductDto;
  onEdit: (data: SubscriptionProductUpdateRequestDto, id: string) => void;
  onBack?: () => void;
}

export const EditForm = ({ subscriptionInfo, onEdit }: EditFormProps) => {
  const { methods, handleSubmit } = useEditForm(subscriptionInfo, onEdit);

  const defaultProps = {
    size: "small" as const,
    error: false,
    helperText: "",
    placeholder: "입력해주세요",
  };

  return (
    <FormProvider {...methods}>
      <Box display="flex" flexDirection="column" gap={4}>
        <Box>
          <Grid container spacing={2}>
            {/* 1. 기본 정보 */}
            <Grid size={{ xs: 12, lg: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                기본 정보
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <FormControl fullWidth>
                <FormLabel required>상품명</FormLabel>
                <TextField
                  {...methods.register("productNm")}
                  {...defaultProps}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <FormControl fullWidth>
                <FormLabel required>상품 유형</FormLabel>
                <TextField
                  select
                  value={methods.watch("productType")}
                  {...methods.register("productType")}
                  {...defaultProps}
                >
                  <MenuItem value="Start">Starter</MenuItem>
                  <MenuItem value="Stand">Standard</MenuItem>
                  <MenuItem value="Pre">Premium</MenuItem>
                </TextField>
              </FormControl>
            </Grid>

            {/* 2. 가격 정보 */}
            <Grid size={{ xs: 12, lg: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                가격 정보
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <FormControl fullWidth>
                <FormLabel required>월간 금액</FormLabel>
                <TextField
                  type="number"
                  {...methods.register("monthlyAmt")}
                  {...defaultProps}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <FormControl fullWidth>
                <FormLabel required>연간 금액</FormLabel>
                <TextField
                  type="number"
                  {...methods.register("firstYearAmt")}
                  {...defaultProps}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <FormControl fullWidth>
                <FormLabel required>통화</FormLabel>
                <TextField
                  select
                  value={methods.watch("currency")}
                  {...methods.register("currency")}
                  {...defaultProps}
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="KRW">KRW</MenuItem>
                </TextField>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <FormControl fullWidth>
                <FormLabel required>선납 기간 (개월)</FormLabel>
                <TextField
                  type="number"
                  {...methods.register("prepaidPeriod")}
                  {...defaultProps}
                />
              </FormControl>
            </Grid>

            {/* 3. 상세 정보 */}
            <Grid size={{ xs: 12, lg: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                상세 정보
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, lg: 12 }}>
              <FormControl fullWidth>
                <FormLabel required>상품 상세 설명</FormLabel>
                <TextField
                  multiline
                  rows={4}
                  {...methods.register("productDtl")}
                  {...defaultProps}
                  placeholder="각 항목은 | 로 구분하여 입력해주세요"
                />
              </FormControl>
            </Grid>

            {/* 4. 활성화 여부 */}
            <Grid size={{ xs: 12, lg: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                활성화 설정
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, lg: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={methods.watch("activeYn") === Yn.Y}
                    onChange={(e) =>
                      methods.setValue(
                        "activeYn",
                        e.target.checked ? Yn.Y : Yn.N
                      )
                    }
                  />
                }
                label="상품 활성화"
              />
            </Grid>
          </Grid>
        </Box>
        <Box display="flex" alignItems="center" justifyContent="center" mt={4}>
          <SaveButton onClick={handleSubmit}>수정</SaveButton>
        </Box>
      </Box>
    </FormProvider>
  );
};
